import { useEffect } from 'react';
import { useEffectiveAccess } from '../stores/accessControlStore';
import { useFilterStore } from '../stores/filterStore';
import type { DataScope } from '../types/accessControl';

function uniq(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function allowedFromScopes(scopes: DataScope[]) {
  // Only allow-rows seed/clamp UI filters; block rows only exclude at match time
  const allowScopes = scopes.filter((s) => (s.mode || 'allow') !== 'block');
  const entityTypes = uniq(
    allowScopes.map((s) => (s.entity_type || '').trim()).filter(Boolean)
  );
  const deviceTypes = uniq(
    allowScopes.map((s) => (s.device_type || '').trim().toLowerCase()).filter(Boolean)
  );
  const courseIds = uniq(
    allowScopes.map((s) => (s.course_id || '').trim()).filter(Boolean)
  );
  return { entityTypes, deviceTypes, courseIds };
}

/**
 * When the user is data-restricted by access-group scopes, clamp filterStore
 * selections (and available option lists) to the allowlist.
 */
export function useAccessDataScopeClamp() {
  const access = useEffectiveAccess();
  const {
    selectedEntityTypes,
    selectedDeviceTypes,
    selectedCourses,
    availableCourses,
    setSelectedEntityTypes,
    setSelectedDeviceTypes,
    setSelectedCourses,
    setAvailableCourses,
  } = useFilterStore();

  useEffect(() => {
    if (!access.data_restricted || !access.data_scopes?.length) {
      return;
    }
    // Only clamp UI filters when there are Allow rows; Block-only should not
    // force-select entity types in the COP filter bar.
    const { entityTypes, deviceTypes, courseIds } = allowedFromScopes(access.data_scopes);
    if (entityTypes.length === 0 && deviceTypes.length === 0 && courseIds.length === 0) {
      return;
    }

    if (entityTypes.length > 0) {
      const next = selectedEntityTypes.filter((t) =>
        entityTypes.some((a) => a.toLowerCase() === t.toLowerCase())
      );
      if (next.length === 0) {
        setSelectedEntityTypes(entityTypes);
      } else if (next.length !== selectedEntityTypes.length) {
        setSelectedEntityTypes(next);
      }
    }

    if (deviceTypes.length > 0) {
      const next = selectedDeviceTypes.filter((t) =>
        deviceTypes.includes(t.toLowerCase())
      );
      if (next.length === 0) {
        setSelectedDeviceTypes(deviceTypes);
      } else if (next.length !== selectedDeviceTypes.length) {
        setSelectedDeviceTypes(next);
      }
    }

    if (courseIds.length > 0) {
      const nextCourses = selectedCourses.filter((c) => courseIds.includes(c));
      if (nextCourses.length === 0) {
        setSelectedCourses(courseIds);
      } else if (nextCourses.length !== selectedCourses.length) {
        setSelectedCourses(nextCourses);
      }
      if (availableCourses.length > 0) {
        const filtered = availableCourses.filter((c) => courseIds.includes(c.id));
        if (filtered.length !== availableCourses.length) {
          setAvailableCourses(
            filtered.length > 0
              ? filtered
              : courseIds.map((id) => ({ id, title: id }))
          );
        }
      }
    }
  }, [
    access.data_restricted,
    access.bypass,
    access.data_scopes,
    selectedEntityTypes,
    selectedDeviceTypes,
    selectedCourses,
    availableCourses,
    setSelectedEntityTypes,
    setSelectedDeviceTypes,
    setSelectedCourses,
    setAvailableCourses,
  ]);
}
